import type {
  StaffDiagnosticsApi,
  StaffOperationsApi,
  StaffPaymentsApi,
} from "@lafayette/api-client";
import {
  type StaffOperationsControlReason,
  type StaffPaymentException,
  type StaffResolvePaymentExceptionBody,
  staffOperationsControlReasons,
  staffPaymentResolutionCodeValues,
} from "@lafayette/schemas";
import { useState } from "react";
import { controlReasonLabels } from "../../operations/labels";
import { SelectField } from "../../ui/fields";
import { exceptionKindLabels, resolutionLabels } from "../labels";
import { RecoveryDialog } from "./RecoveryDialog";

// The four recovery dialogs of the diagnostics area (CHT-772). Each one sends a SUPPORTED command of the API through its own client, and
// nothing else: no dialog edits a provider or a database row. The API decides the role again (re-run, pause and resume: administrator;
// reconcile: a supervisor or a concierge with a relationship; resolve an exception: supervisor).

type Done = Readonly<{
  onDone: (message: string) => void;
  onReload: () => void;
  onClose: () => void;
}>;

const REASON_OPTIONS = staffOperationsControlReasons.map((value) => ({
  value,
  label: controlReasonLabels[value],
}));
const isReason = (value: string): value is StaffOperationsControlReason =>
  (staffOperationsControlReasons as readonly string[]).includes(value);

function ReasonCodeField({
  value,
  onChange,
}: Readonly<{
  value: StaffOperationsControlReason;
  onChange: (value: StaffOperationsControlReason) => void;
}>) {
  return (
    <SelectField
      id="diagnostics-reason-code"
      label="Reason"
      value={value}
      onChange={(next) => {
        if (isReason(next)) onChange(next);
      }}
      options={REASON_OPTIONS}
      required
    />
  );
}

// --- re-run one failed job ---------------------------------------------------------------------------------------------------------

export function RequeueDialog({
  api,
  jobId,
  taskLabel,
  onDone,
  onReload,
  onClose,
}: Done & Readonly<{ api: StaffOperationsApi; jobId: string; taskLabel: string | null }>) {
  const [reason, setReason] = useState<StaffOperationsControlReason>("INCIDENT");
  return (
    <RecoveryDialog
      title={taskLabel === null ? "Re-run this job" : `Re-run this ${taskLabel} job`}
      description="This makes one new job for the same subject. The failed job stays in the list as the record. The new job follows the same rules as the first: a re-run does not skip a rule, and it does not change the provider or the data by hand."
      confirmLabel="Re-run job"
      onClose={onClose}
      onReload={onReload}
      onDone={onDone}
      build={() => ({
        ok: true,
        send: async (_reason, key) => {
          await api.requeueJob(jobId, { reasonCode: reason }, { idempotencyKey: key });
          return { ok: true, message: `${taskLabel ?? "The job"} was queued again as a new job.` };
        },
      })}
    >
      <ReasonCodeField value={reason} onChange={setReason} />
    </RecoveryDialog>
  );
}

// --- pause or resume a task family -------------------------------------------------------------------------------------------------

export function ControlDialog({
  api,
  taskId,
  label,
  pause,
  onDone,
  onReload,
  onClose,
}: Done & Readonly<{ api: StaffOperationsApi; taskId: string; label: string; pause: boolean }>) {
  const [reason, setReason] = useState<StaffOperationsControlReason>("INCIDENT");
  const all = taskId === "*";
  return (
    <RecoveryDialog
      title={pause ? `Pause ${label}` : `Resume ${label}`}
      description={
        pause
          ? all
            ? "This stops EVERY background task at once, with no deploy. The jobs wait in Postgres and run again after a resume. Use it only in a real incident."
            : "This stops new work of this task at once, with no deploy. Its jobs wait in Postgres and run again after a resume. Nothing is deleted."
          : "The task runs again. The jobs that waited are claimed again."
      }
      confirmLabel={pause ? "Pause" : "Resume"}
      tone={pause && all ? "danger" : "primary"}
      onClose={onClose}
      onReload={onReload}
      onDone={onDone}
      build={() => ({
        ok: true,
        send: async (_reason, key) => {
          const result = await api.setJobControl(
            taskId,
            pause ? { paused: true, reasonCode: reason } : { paused: false },
            { idempotencyKey: key },
          );
          return {
            ok: true,
            message: pause
              ? result.changed
                ? `${label} is paused.`
                : `${label} was paused already.`
              : result.changed
                ? `${label} is running again.`
                : `${label} was not paused.`,
          };
        },
      })}
    >
      {pause ? <ReasonCodeField value={reason} onChange={setReason} /> : null}
    </RecoveryDialog>
  );
}

// --- reconcile one payment ---------------------------------------------------------------------------------------------------------

export function ReconcileDialog({
  payments,
  paymentRequestId,
  onDone,
  onReload,
  onClose,
}: Done & Readonly<{ payments: StaffPaymentsApi; paymentRequestId: string }>) {
  return (
    <RecoveryDialog
      title="Reconcile this payment"
      description="This asks the payment provider what happened to this payment and records the answer as evidence. It is safe to repeat: the provider ids make it idempotent. It moves no money. It never marks a payment paid without verified provider evidence, and it never confirms a supplier request."
      confirmLabel="Reconcile"
      onClose={onClose}
      onReload={onReload}
      onDone={onDone}
      build={() => ({
        ok: true,
        send: async (_reason, key) => {
          const outcome = await payments.reconcile(paymentRequestId, { idempotencyKey: key });
          if (outcome.state === "FAILED") {
            // The provider could not answer. The action did not do its job: it stays on screen and the incident is NOT resolved.
            return {
              ok: false,
              message:
                "The provider could not answer. Nothing was applied from a partial answer. This payment is not checked yet. Try once more later, and follow the runbook if it fails again.",
            };
          }
          const label = outcome.evidence === "NONE" ? "no provider" : outcome.evidence;
          return {
            ok: true,
            message: `Reconcile finished (evidence: ${label}): ${outcome.examined} examined, ${outcome.applied} applied, ${outcome.duplicates} already known, ${outcome.exceptions} exceptions.`,
          };
        },
      })}
    />
  );
}

// --- resolve one payment exception -------------------------------------------------------------------------------------------------

export function ResolveExceptionDialog({
  api,
  exception,
  onDone,
  onReload,
  onClose,
}: Done &
  Readonly<{
    api: Pick<StaffDiagnosticsApi, "resolvePaymentException">;
    exception: StaffPaymentException;
  }>) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const closesPayment = exception.kind === "REQUEST_WITHDRAWN" && code === "REFUNDED_OUT_OF_BAND";
  return (
    <RecoveryDialog
      title={`Resolve: ${exceptionKindLabels[exception.kind]}`}
      description="This records your decision. It moves no money and confirms nothing. If money moved, refund it in the provider dashboard FIRST, then record that here. If someone else changed this exception, you will be told and nothing is written."
      confirmLabel="Resolve exception"
      reason="text"
      onClose={onClose}
      onReload={onReload}
      onDone={onDone}
      build={() => {
        if (!(staffPaymentResolutionCodeValues as readonly string[]).includes(code)) {
          setError("Choose a decision.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          send: async (reason, key) => {
            await api.resolvePaymentException(
              exception.id,
              {
                expectedVersion: exception.version,
                resolutionCode: code as StaffResolvePaymentExceptionBody["resolutionCode"],
                reason,
              },
              { idempotencyKey: key },
            );
            return { ok: true, message: "The exception is resolved and the decision is recorded." };
          },
        };
      }}
    >
      <SelectField
        id="diagnostics-resolution"
        label="Decision"
        value={code}
        placeholder="Choose a decision"
        onChange={setCode}
        options={staffPaymentResolutionCodeValues.map((value) => ({
          value,
          label: resolutionLabels[value],
        }))}
        error={error}
        required
        hint={
          closesPayment
            ? "A payment that still waits for payment is closed as cancelled, in the same step."
            : undefined
        }
      />
    </RecoveryDialog>
  );
}
